// ** Component Imports
import { Heading, Typography } from '@/shared/ui'

// ** Type Imports
import { type MyPlan } from '../../model/types'

// ** Utility Imports
import {
	formatMoneyFromCents,
	formatTierRange,
	getBillingPeriodLabel,
	getTiersModeLabel
} from '../../lib/customPricing'

// ** Styled Components
import {
	DetailRow,
	Details,
	HeadlinePrice,
	MutedText,
	NoteBox,
	TiersTable,
	UnavailableBox
} from './styles'

const BILLING_PERIOD_SUFFIX: Record<string, string> = {
	MONTHLY: 'per month',
	SEMI_ANNUAL: 'every 6 months',
	YEARLY: 'per year'
}

interface IProps {
	plan: MyPlan
	// the pre-checkout screens already show the note in their own layout
	showNote?: boolean
}

/**
 * Renders the terms of a negotiated plan, read live from Stripe. Shared by the
 * pre-checkout screens and the billing tab so both describe the plan the same way.
 */
export const CustomPlanDetails = ({ plan, showNote = true }: IProps) => {
	// ** Variables
	const { price, customMaxClientCount, customPlanNote } = plan
	const tiers = price?.tiers ?? []
	const isUsageBased = price?.planType === 'USAGE_BASED'
	const periodSuffix =
		price?.billingPeriod !== null && price?.billingPeriod !== undefined
			? BILLING_PERIOD_SUFFIX[price.billingPeriod]
			: undefined

	return (
		<Details>
			{/* A price archived or deleted in the Stripe dashboard cannot be read
			    back — the note is then the only description of what was agreed */}
			{price === null ? (
				<UnavailableBox>
					<Typography variant='body2'>
						Your plan details are temporarily unavailable. Your agreed terms are
						unchanged — please contact support if you need them confirmed.
					</Typography>
				</UnavailableBox>
			) : (
				<>
					{!isUsageBased && (
						<HeadlinePrice>
							<Heading variant='h2' weight='bold'>
								{formatMoneyFromCents(price.amount, price.currency)}
							</Heading>
							{periodSuffix !== undefined && (
								<MutedText weight='regular'>{periodSuffix}</MutedText>
							)}
						</HeadlinePrice>
					)}

					{isUsageBased && tiers.length > 0 && (
						<>
							<MutedText variant='body2' weight='medium'>
								{`Billed by client count, ${getBillingPeriodLabel(
									price.billingPeriod
								).toLowerCase()}`}
							</MutedText>
							<TiersTable>
								<thead>
									<tr>
										<th>Clients</th>
										<th>Per client</th>
										<th>Flat fee</th>
									</tr>
								</thead>
								<tbody>
									{tiers.map((tier, index) => (
										<tr key={`${tier.upTo ?? 'inf'}-${index}`}>
											<td>
												{formatTierRange(
													tier,
													index > 0 ? tiers[index - 1].upTo : null
												)}
											</td>
											<td>
												{formatMoneyFromCents(tier.unitAmount, price.currency)}
											</td>
											<td>
												{formatMoneyFromCents(tier.flatAmount, price.currency)}
											</td>
										</tr>
									))}
								</tbody>
							</TiersTable>
							<MutedText variant='body3'>
								{price.tiersMode === 'volume'
									? 'Your whole client count is billed at the single tier it falls into.'
									: 'Each portion of your client count is billed at its own tier’s rate.'}
							</MutedText>
						</>
					)}

					{!isUsageBased && price.billingPeriod !== null && (
						<DetailRow>
							<Typography variant='body2' weight='semi-bold'>
								Billing period
							</Typography>
							<MutedText variant='body2'>
								{getBillingPeriodLabel(price.billingPeriod)}
							</MutedText>
						</DetailRow>
					)}

					{isUsageBased && price.tiersMode !== null && (
						<DetailRow>
							<Typography variant='body2' weight='semi-bold'>
								Tiering
							</Typography>
							<MutedText variant='body2'>
								{getTiersModeLabel(price.tiersMode)}
							</MutedText>
						</DetailRow>
					)}
				</>
			)}

			{/* Only a flat-rate plan carries a hard cap; usage-based bills by count */}
			<DetailRow>
				<Typography variant='body2' weight='semi-bold'>
					Client limit
				</Typography>
				<MutedText variant='body2'>
					{customMaxClientCount !== null
						? `${customMaxClientCount} clients`
						: 'No limit'}
				</MutedText>
			</DetailRow>

			{showNote &&
				customPlanNote !== null &&
				customPlanNote.trim().length > 0 && (
					<NoteBox>
						<Typography variant='body2'>{customPlanNote}</Typography>
					</NoteBox>
				)}
		</Details>
	)
}
